import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  FormHelperText,
  CircularProgress,
  Switch,
  FormControlLabel,
  Paper,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../../../../services/api';
import { t } from '../../../../utils/textUtils';
import MasterJobSelector from '../../../../components/gantt/MasterJobSelector';
import { useInstallationTeams } from '../hooks/useInstallationTeams';

// Constants
const UNITS = [
  { value: 'm3', label: t('M³ (KUBIK)') },
  { value: 'm2', label: t('M² (PERSEGI METER)') },
  { value: 'm', label: t('M (METER LINIER)') },
  { value: 'ls', label: t('LS (LUMPSUM)') },
  { value: 'unit', label: t('UNIT') },
  { value: 'pcs', label: t('PCS') },
  { value: 'set', label: t('SET') },
  { value: 'titik', label: t('TITIK') },
  { value: 'EA', label: t('EA (EACH)') }
];

export default function AddTaskDialog({ 
  open, 
  onClose, 
  onSave, 
  loading = false, 
  categoryData = null,
  projectId 
}) {
  const [formData, setFormData] = useState({
    name: '',
    startDate: new Date(),
    endDate: new Date(new Date().setDate(new Date().getDate() + 1)),
    progress: 0,
    quantity: 0,
    completedQuantity: 0,
    unit: '',
    unitPrice: 0,
    autoCalculateWeight: false,
    weight: 0,
    notes: '',
    assignedTo: '',
    displayOrder: 0,
    isMilestone: false,
    dependencies: [],
    masterJobId: null,
    jobCode: '',
    status: 'to_do'
  });

  const [errors, setErrors] = useState({});
  const [projectTasks, setProjectTasks] = useState([]);
  
  // Use custom hook for installation teams
  const { teams: teamMembers, loading: teamsLoading, error: teamsError } = useInstallationTeams();

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 7);
      
      setFormData({
        name: '',
        startDate: today,
        endDate: tomorrow,
        progress: 0,
        quantity: 0,
        completedQuantity: 0,
        unit: '',
        unitPrice: 0,
        autoCalculateWeight: false,
        weight: 0,
        notes: '',
        assignedTo: '',
        displayOrder: 0,
        isMilestone: false,
        dependencies: [],
        masterJobId: null,
        jobCode: '',
        status: 'to_do'
      });
      setErrors({});
    }
  }, [open]);

  // Fetch project tasks
  useEffect(() => {
    if (open && projectId) {
      fetchProjectTasks();
    }
  }, [open, projectId]);

  const fetchProjectTasks = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/gantt/flat`);
      if (response.data && Array.isArray(response.data)) {
        setProjectTasks(response.data);
      }
    } catch (err) {
      console.error('Error fetching project tasks:', err);
      setProjectTasks([]);
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const calculateProgress = (completed, total, weight) => {
    if (!total || total <= 0 || !weight) return 0;
    const rawProgress = (completed / total) * weight;
    return Math.min(rawProgress, weight); // Progress can't exceed weight
  };

  // Auto-calculate weight when autoCalculateWeight is enabled
  useEffect(() => {
    if (formData.autoCalculateWeight && formData.quantity > 0 && formData.unitPrice > 0) {
      const totalCost = formData.quantity * formData.unitPrice;
      // This would need project total cost to calculate percentage
      // For now, we'll use a simple calculation based on cost
      const calculatedWeight = Math.min(100, (totalCost / 1000000) * 10); // Example calculation
      const newWeight = parseFloat(calculatedWeight.toFixed(2));
      
      // Only update if the weight has actually changed to prevent infinite loop
      setFormData(prev => {
        if (prev.weight !== newWeight) {
          return { ...prev, weight: newWeight };
        }
        return prev;
      });
    }
  }, [formData.autoCalculateWeight, formData.quantity, formData.unitPrice]);

  const handleDateChange = (name, date) => {
    setFormData(prev => ({ ...prev, [name]: date }));
    
    // Auto-calculate duration when dates change
    if (name === 'startDate' && formData.endDate) {
      const duration = calculateDuration(date, formData.endDate);
      setFormData(prev => ({ ...prev, duration }));
    } else if (name === 'endDate' && formData.startDate) {
      const duration = calculateDuration(formData.startDate, date);
      setFormData(prev => ({ ...prev, duration }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays + 1); // +1 to include both start and end dates
  };

  const handleSliderChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMasterJobChange = async (masterJobId) => {
    setFormData(prev => ({ ...prev, masterJobId }));
    
    if (masterJobId) {
      try {
        const response = await api.get(`/master-jobs/${masterJobId}`);
        
        if (response.data?.success) {
          const masterJob = response.data.data;
          
          setFormData(prev => ({
            ...prev,
            name: masterJob.nama_pekerjaan,
            quantity: masterJob.volume_default || 0,
            unit: masterJob.satuan || '',
            jobCode: masterJob.kode_pekerjaan || ''
          }));
          
          if (errors.name) {
            setErrors(prev => ({ ...prev, name: null }));
          }
        }
      } catch (error) {
        console.error('Error fetching master job details:', error);
      }
    } else {
      setFormData(prev => ({ ...prev, jobCode: '', name: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nama pekerjaan wajib diisi';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Tanggal mulai wajib diisi';
    }
    
    if (!formData.endDate) {
      newErrors.endDate = 'Tanggal selesai wajib diisi';
    }
    
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = 'Tanggal selesai harus setelah tanggal mulai';
    }
    
    if (formData.weight < 0 || formData.weight > 100) {
      newErrors.weight = 'Bobot harus antara 0 dan 100';
    }
    
    if (formData.progress < 0 || formData.progress > 100) {
      newErrors.progress = 'Progress harus antara 0 dan 100';
    }

    if (formData.quantity < 0) {
      newErrors.quantity = 'Volume tidak boleh negatif';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    if (!categoryData) {
      console.error('Category data is required');
      return;
    }

    const formatDate = (date) => {
      if (!date) return null;
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    };

    const payload = {
      name: formData.name.trim(),
      startDate: formatDate(formData.startDate),
      endDate: formatDate(formData.endDate),
      progress: parseFloat(formData.progress) || 0,
      quantity: parseFloat(formData.quantity) || 0,
      completedQuantity: parseFloat(formData.completedQuantity) || 0,
      unit: formData.unit || null,
      unitPrice: parseFloat(formData.unitPrice) || 0,
      autoCalculateWeight: formData.autoCalculateWeight,
      weight: parseFloat(formData.weight) || 0,
      notes: formData.notes.trim(),
      assignedTo: formData.assignedTo || null,
      displayOrder: formData.displayOrder || 0,
      isMilestone: formData.isMilestone,
      dependencies: formData.dependencies || [],
      masterJobId: formData.masterJobId,
      jobCode: formData.jobCode || '',
      status: formData.status || 'to_do'
    };

    onSave(payload);
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        startDate: new Date(),
        endDate: new Date(new Date().setDate(new Date().getDate() + 1)),
        progress: 0,
        quantity: 0,
        completedQuantity: 0,
        unit: '',
        unitPrice: 0,
        autoCalculateWeight: false,
        weight: 0,
        notes: '',
        assignedTo: '',
        displayOrder: 0,
        isMilestone: false,
        dependencies: [],
        masterJobId: null,
        jobCode: '',
        status: 'to_do'
      });
      setErrors({});
      onClose();
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return '#4CAF50';
    if (progress >= 50) return '#F0C14B';
    if (progress > 0) return '#FF9800';
    return '#9E9E9E';
  };


  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, height: '90vh', maxWidth: '800px' }
      }}
    >
      <DialogTitle sx={{ pb: 1, bgcolor: '#4A90E2', color: 'white' }}>
        <Typography variant="h6" component="div">
          {categoryData?.unitName || 'Unit'} - {categoryData?.name || 'Kategori'} - Tambah Pekerjaan
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 2, overflow: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          {/* Master Work Item Reference */}
          <Box sx={{ mt: 2,mb: 4, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#4A90E2', fontWeight: 600 }}>
              Referensi Item Pekerjaan Master
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Mulai dengan memilih Item Pekerjaan Master untuk mengisi detail tugas secara otomatis
            </Typography>
            
            <MasterJobSelector
              onChange={handleMasterJobChange}
              value={formData.masterJobId}
              kategori={null}
            />
          </Box>

          {/* Detail Item Pekerjaan */}
          <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'text.primary', fontWeight: 600 }}>
              DETAIL ITEM PEKERJAAN
            </Typography>
            
            <Grid container spacing={3}>
              {/* Nama Item Pekerjaan */}
              <Grid item xs={12} md={9}>
                <TextField
                  name="name"
                  label="Nama Item Pekerjaan"
                  value={formData.name}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  error={!!errors.name}
                  helperText={errors.name || "Masukkan nama item pekerjaan atau pilih dari Item Pekerjaan Master"}
                  disabled={loading}
                  placeholder="Masukkan nama item pekerjaan atau pilih dari Item Pekerjaan Master"
                />
              </Grid>

              {/* Milestone Toggle */}
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', height: '100%', pt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isMilestone"
                        checked={formData.isMilestone || false}
                        onChange={handleSwitchChange}
                        disabled={loading}
                      />
                    }
                    label="Milestone"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Periode Waktu */}
          <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'text.primary', fontWeight: 600 }}>
              PERIODE WAKTU
            </Typography>
            
            <Grid container spacing={3}>
              {/* Start Date */}
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Tanggal Mulai *
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.startDate}
                    onChange={(date) => handleDateChange('startDate', date)}
                    disabled={loading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.startDate,
                        helperText: errors.startDate
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* End Date */}
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Tanggal Selesai *
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.endDate}
                    onChange={(date) => handleDateChange('endDate', date)}
                    minDate={formData.startDate}
                    disabled={loading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.endDate,
                        helperText: errors.endDate
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Duration (Auto-calculated) */}
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Durasi (hari)
                </Typography>
                <TextField
                  name="duration"
                  value={calculateDuration(formData.startDate, formData.endDate)}
                  fullWidth
                  disabled
                  InputProps={{
                    readOnly: true,
                    style: { backgroundColor: '#f5f5f5' }
                  }}
                  helperText="Otomatis dihitung dari tanggal mulai dan selesai"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Kuantitas & Progress Item Pekerjaan */}
          <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'text.primary', fontWeight: 600 }}>
              KUANTITAS & PROGRESS ITEM PEKERJAAN
            </Typography>
            
            <Grid container spacing={2}>
              {/* Total Kuantitas */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Total Kuantitas"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  type="number"
                  fullWidth
                  InputProps={{ 
                    inputProps: { min: 0, step: 0.01 },
                    endAdornment: formData.unit ? (
                      <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        {formData.unit}
                      </Typography>
                    ) : null
                  }}
                  error={!!errors.quantity}
                  helperText={errors.quantity || "Total volume pekerjaan"}
                  size="small"
                  disabled={loading}
                />
              </Grid>

              {/* Satuan */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Satuan</InputLabel>
                  <Select
                    label="Satuan"
                    name="unit"
                    value={formData.unit || ''}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    {UNITS.map((unit) => (
                      <MenuItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Satuan pengukuran</FormHelperText>
                </FormControl>
              </Grid>

              {/* Harga Satuan */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Harga Satuan"
                  name="unitPrice"
                  value={formData.unitPrice}
                  onChange={handleInputChange}
                  type="number"
                  fullWidth
                  InputProps={{ 
                    inputProps: { min: 0, step: 1000 },
                    startAdornment: <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary' }}>Rp</Typography>
                  }}
                  error={!!errors.unitPrice}
                  helperText={errors.unitPrice || "Harga satuan dari BOQ"}
                  size="small"
                  disabled={loading}
                />
              </Grid>

              {/* Total Cost */}
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      border: '1px dashed rgba(25, 118, 210, 0.5)',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        Total Cost:
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, ml: 1 }}>
                        {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(formData.quantity * formData.unitPrice)}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Grid>

              {/* Auto Calculate Weight Switch */}
              <Grid item xs={12} sm={4}>
                <Box sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'flex-start', 
                  pt: 1
                }}>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={formData.autoCalculateWeight} 
                        onChange={handleSwitchChange} 
                        name="autoCalculateWeight"
                        color="primary"
                        disabled={loading}
                      />
                    }
                    label="Hitung Bobot Otomatis"
                  />
                  <FormHelperText sx={{ ml: 2 }}>
                    {formData.autoCalculateWeight ? 
                      "Bobot dihitung otomatis berdasarkan volume × harga" : 
                      "Aktifkan untuk menghitung bobot secara otomatis"}
                  </FormHelperText>
                </Box>
              </Grid>

              {/* Weight */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Bobot (%)"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  type="number"
                  fullWidth
                  InputProps={{ 
                    inputProps: { min: 0, max: 100, step: 0.01 },
                    endAdornment: <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>%</Typography>,
                    readOnly: formData.autoCalculateWeight
                  }}
                  disabled={formData.autoCalculateWeight || loading}
                  error={!!errors.weight}
                  helperText={formData.autoCalculateWeight ? "Bobot dihitung otomatis berdasarkan volume × harga" : "Bobot pekerjaan terhadap total project (0-100%)"}
                  size="small"
                />
              </Grid>

              {/* Progress */}
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Progress (%)"
                  name="progress"
                  value={formData.progress}
                  onChange={handleInputChange}
                  type="number"
                  fullWidth
                  InputProps={{ 
                    inputProps: { min: 0, max: 100, step: 0.01 },
                    endAdornment: <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>%</Typography>
                  }}
                  error={!!errors.progress}
                  helperText={errors.progress || 
                    `Kontribusi ke project: ${(formData.progress / 100 * formData.weight).toFixed(2)}%`}
                  size="small"
                  disabled={loading}
                />
              </Grid>

              {/* S-Curve Impact */}
              <Grid item xs={12} sm={8}>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: '#f5f9ff', 
                    height: '100%', 
                    display: 'flex', 
                    alignItems: 'center',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2">
                    <strong>S-Curve Impact:</strong> {formData.quantity > 0 && formData.weight > 0 
                      ? `Setiap 1 ${formData.unit || 'unit'} = ${(formData.weight / formData.quantity).toFixed(4)}% dari project` 
                      : 'Set quantity dan weight untuk melihat impact'}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          {/* Subkontraktor & Catatan */}
          <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'text.primary', fontWeight: 600 }}>
              SUBKONTRAKTOR & CATATAN
            </Typography>
            
            <Grid container spacing={3}>
              {/* Subcontractor */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <TextField
                    select
                    name="assignedTo"
                    label="Subkontraktor"
                    value={formData.assignedTo || ''}
                    onChange={handleInputChange}
                    disabled={loading || teamsLoading}
                    error={!!teamsError}
                    helperText={teamsError ? "Error loading teams" : "Pilih subkontraktor untuk tugas ini"}
                  >
                    <MenuItem value="">
                      <em>{teamsLoading ? "Loading teams..." : "Pilih subkontraktor untuk tugas ini"}</em>
                    </MenuItem>
                    {teamMembers.map((team) => (
                      <MenuItem key={team.id} value={team.id.toString()}>
                        {team.name}
                      </MenuItem>
                    ))}
                    {teamsError && (
                      <MenuItem disabled>
                        <em>Error loading installation teams</em>
                      </MenuItem>
                    )}
                  </TextField>
                </FormControl>
              </Grid>

              {/* Catatan */}
              <Grid item xs={12} sm={6}>
                <TextField
                  name="notes"
                  label="Catatan"
                  value={formData.notes}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={loading}
                  placeholder="Tambahkan catatan untuk tugas ini..."
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{ minWidth: 80 }}
        >
          Batal
        </Button>
        <Button 
          onClick={handleSave}
          disabled={loading || !formData.name.trim()}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{ minWidth: 80 }}
        >
          Simpan
        </Button>
      </DialogActions>
    </Dialog>
  );
}
